<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  coverageAlertsApi, COVERAGE_ALERT_REASONS, type CoverageAlert, type CoverageAlertReason,
} from '@/api/coverage-alerts';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import { apiErrorText } from '@/utils/api-errors';

// Closing an open alert by hand: a reason, and a note when the reason is "Otro".
const props = defineProps<{ alert: CoverageAlert | null }>();
const emit = defineEmits<{ close: [] }>();

const reason = ref<CoverageAlertReason | null>(null);
const note = ref('');
const errorText = ref('');

watch(() => props.alert, () => {
  reason.value = null;
  note.value = '';
  errorText.value = '';
});

const open = computed({
  get: () => props.alert !== null,
  set: (value: boolean) => {
    if (!value) emit('close');
  },
});
const noteRequired = computed(() => reason.value === 'other');
const canSubmit = computed(() => (
  reason.value !== null && (!noteRequired.value || note.value.trim() !== '')
));

const queryClient = useQueryClient();
const { mutate: dismiss, isPending } = useMutation({
  mutationFn: () => coverageAlertsApi.dismiss(props.alert!.id, reason.value!, note.value.trim()),
  onSuccess: () => emit('close'),
  onError: (error) => {
    errorText.value = apiErrorText(error, 'No se pudo cerrar la alerta.');
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['coverageAlerts'] });
  },
});
</script>

<template>
  <responsive-dialog
    v-model="open"
    max-width="560"
    title="Cerrar alerta"
  >
    <div
      v-if="alert"
      class="text-body-2 mb-4"
    >
      {{ alert.patientFullName }} · {{ alert.humanCareer }} · {{ alert.humanSlotStartsAt }}
    </div>
    <v-select
      v-model="reason"
      :items="COVERAGE_ALERT_REASONS"
      label="Motivo"
      variant="outlined"
      density="compact"
    />
    <v-textarea
      v-model="note"
      :label="noteRequired ? 'Nota (obligatoria)' : 'Nota (opcional)'"
      variant="outlined"
      density="compact"
      rows="3"
      auto-grow
    />
    <v-alert
      v-if="errorText"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-2"
    >
      {{ errorText }}
    </v-alert>
    <template #actions>
      <v-spacer />
      <v-btn
        variant="text"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!canSubmit"
        :loading="isPending"
        @click="dismiss()"
      >
        Cerrar alerta
      </v-btn>
    </template>
  </responsive-dialog>
</template>
